import { Fragment, type ReactNode } from 'react';

import { flexRender, type Row } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';

import { TableBody, TableCell, TableRow } from '../../primitives/table';
import { EmptyTableState } from './empty-table-state';
import { VirtualTableSpacer } from './virtual-table-spacer';

export type VirtualTableBodyRenderRowArgs<TData> = {
    row: Row<TData>;
    defaultRow: ReactNode;
    colSpan: number;
};

type VirtualTableBodyProps<TData> = {
    status: 'pending' | 'error' | 'success';
    rows: Row<TData>[];
    virtualItems: VirtualItem[];
    totalSize: number;
    colSpan: number;
    isLoadingMore: boolean;
    isEmpty: boolean;
    loadingRow: ReactNode;
    skeleton: ReactNode;
    emptyMessage?: string;
    /** Additive, passed through to `EmptyTableState`'s own `className`. */
    emptyStateClassName?: string;
    renderRow?: (args: VirtualTableBodyRenderRowArgs<TData>) => ReactNode;
};

export function VirtualTableBody<TData>({
    status,
    rows,
    virtualItems,
    totalSize,
    colSpan,
    isLoadingMore,
    isEmpty,
    loadingRow,
    skeleton,
    emptyMessage,
    emptyStateClassName,
    renderRow,
}: VirtualTableBodyProps<TData>) {
    const topSpacerHeight =
        virtualItems.length > 0 && virtualItems[0].index > 0 ? virtualItems[0].start : 0;

    const bottomSpacerHeight =
        virtualItems.length > 0 ? totalSize - (virtualItems[virtualItems.length - 1]?.end || 0) : 0;

    return (
        <TableBody>
            <VirtualTableSpacer height={topSpacerHeight} colSpan={colSpan} />

            {status === 'pending' && (
                <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={colSpan}>{skeleton}</TableCell>
                </TableRow>
            )}

            {status === 'success' && isEmpty && (
                <EmptyTableState
                    colSpan={colSpan}
                    message={emptyMessage}
                    className={emptyStateClassName}
                />
            )}

            {status === 'success' &&
                !isEmpty &&
                virtualItems.map((virtualRow) => {
                    // Handle loading indicator for infinite scroll
                    if (virtualRow.index === rows.length && isLoadingMore) {
                        return <Fragment key={virtualRow.key}>{loadingRow}</Fragment>;
                    }

                    const row = rows[virtualRow.index];
                    if (!row) return null;

                    const defaultRow = (
                        <TableRow className="table-row">
                            {row.getVisibleCells().map((cell) => (
                                <TableCell
                                    key={cell.id}
                                    className={cell.column.columnDef.meta?.className}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    );

                    const rendered = renderRow
                        ? renderRow({ row, defaultRow, colSpan })
                        : defaultRow;

                    return <Fragment key={row.id}>{rendered}</Fragment>;
                })}

            <VirtualTableSpacer height={bottomSpacerHeight} colSpan={colSpan} />
        </TableBody>
    );
}
